import { SVGS } from '@/assets/svgs'
import { AlbumPhoto, Ego, Note } from '@/types'
import { DeleteOutline, EditOutlined, Favorite, MoreVert } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, List, Typography } from '@mui/material'
import SVG from 'react-inlinesvg'
import { AlbumPhotoItem, NoteWrapper } from '../__styled'
import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react'
import { useDeleteNote, useInteractToNote } from '@/hooks/api/dashboard'
import { toast } from 'react-hot-toast'
import { ActionMenu, RModal } from '@/components'
import colors from '@/styles/colors'
import ImageSlider from '../components/ImageSlider'
import { useBrowserQuery, useLocalStorage } from '@/hooks/common'
import { useLocation, useNavigate } from 'react-router-dom'
import AlbumCollage from '@/components/AlbumCollage/AlbumCollage'
import { scrollToBottom } from '@/utils/helper'
import { BackgroundImage } from '@/pages/__styled'
import { IMAGES } from '@/assets/images'
import { COLLAGE_ITEMS_LIMIT } from '@/components/AlbumCollage/Config'
import { TopicDetailsContext } from '@/context/TopicDetails'
import LoadingAnimatedView from '@/components/LoadingAnimatedView'
import { RoundedFilledButton } from '@/components/StyledComponents'
import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import { NoDataPlaceholders } from '@/utils/placeholders'

interface ContentProps {
  photos: Record<string, AlbumPhoto>
  notes?: Note[]
  onClickAlbumNote?: (note: Note) => void
}

export default function AlbumContent({
  notes: topicNotes = [],
  photos,
  onClickAlbumNote,
}: ContentProps) {
  const [slideMode, setSlideMode] = useState(false)
  const [modalOpened, setModalOpened] = useState(false)
  const [deleteNoteID, setDeleteNoteID] = useState('')

  const interactToNote = useInteractToNote()
  const navigate = useNavigate()
  const query = useBrowserQuery()
  const { pathname } = useLocation()
  const [ewUser] = useLocalStorage('ewUser', null)
  const topicID = query.get('topicID') || ''
  const noteID = query.get('noteID') || ''
  const [loadingMore, setLoadingMore] = useState(false)

  const currentPageNumber = useRef<Number | null>(null)
  const deleteNote = useDeleteNote(false)
  const { topic, canLoadNextPage, loadNextPage } = useContext(TopicDetailsContext)
  const isMyTopic = topic.userID == ewUser?.userID

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scrollRef = useRef<any>(null)
  let notesOldSize = useRef<number | null>(null)

  const notes: Note[] = useMemo(() => {
    if (topic && topic.notes.length > 0) {
      return topic.notes.filter((e) => e.noteID !== topic.firstNote.noteID)
    } else {
      return []
    }
  }, [topic])

  const selectedNote = useMemo(() => {
    const find = notes.find((i) => i.noteID === noteID)
    return find
  }, [notes, noteID])

  const onClickNoteLike = (pNote: Note) => {
    toast.promise(
      interactToNote.mutateAsync({
        interactionType: '3',
        interaction: pNote?.isThumbsUp == 1 ? '0' : '1',
        comment: 'test',
        noteID: pNote.noteID,
        topicID,
      }),
      {
        loading: `${pNote?.isThumbsUp == 1 ? 'Disliking' : 'Liking'} a message...`,
        success: <Typography variant='p'>Success!</Typography>,
        error: (
          <Typography variant='p'>
            Failed to {pNote?.isThumbsUp == 1 ? 'dislike' : 'like'}
          </Typography>
        ),
      },
    )
  }

  useEffect(() => {
    if (notes.length === 0) {
      query.delete('noteID')
      navigate(`${pathname}?${query.toString()}`, { replace: true })
    } else if (noteID.length === 0 || !notes.find((i) => i.noteID === noteID)) {
      // query.set('noteID', notes[notes.length - 1].noteID)
      // navigate(`${pathname}?${query.toString()}`, { replace: true })
    }

    let shouldScrollToBottom =
      currentPageNumber.current == null ||
      currentPageNumber.current == topic.pagination?.currentPage
    currentPageNumber.current = topic.pagination?.currentPage || 1

    if (shouldScrollToBottom) {
      // First time,
      if (!notesOldSize.current) {
        scrollToBottom(scrollRef, 500)
      } else if (notesOldSize.current < notes.length) {
        // When note added
        scrollToBottom(scrollRef, 50)
      }
    }

    notesOldSize.current = notes.length
  }, [notes])

  const onClickNote = (nID: string) => {
    if (noteID != nID) {
      query.set('noteID', nID)
      navigate(`${pathname}?${query.toString()}`, { replace: true })
    }
  }

  const onCloseSlider = () => {
    setSlideMode(false)
  }

  const onCloseModal = () => {
    setModalOpened(false)
  }

  const onClickEditNote = (nID: string) => {
    query.set('status', 'edit')
    query.set('noteID', nID)
    navigate(`${pathname}?${query.toString()}`)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onClickDeleteNote = (noteID: string) => {
    setDeleteNoteID(noteID)
    setModalOpened(true)
  }

  const noteActions = useCallback((note: Note) => {
    return [
      {
        icon: <EditOutlined />,
        name: 'Edit',
        onClick: () => onClickEditNote(note.noteID),
      },
      {
        icon: <DeleteOutline />,
        name: 'Delete',
        onClick: () => onClickDeleteNote(note.noteID),
      },
    ]
  }, [])

  const onDeletePhoto = () => {
    if (!deleteNoteID) return
    toast.promise(
      deleteNote.mutateAsync({
        noteID: deleteNoteID,
        topicID: topicID,
      }),
      {
        loading: 'Deleting...',
        success: <Typography variant='p'>Successfully deleted</Typography>,
        error: <Typography variant='p'>Failed to delete</Typography>,
      },
    )
  }

  if (notes.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          mb: '60px',
        }}
      >
        <NoDataPlaceholder
          {...(isMyTopic
            ? NoDataPlaceholders.emptyAlbumTopicOwner
            : NoDataPlaceholders.emptyAlbumTopicVisitor)}
        />
      </Box>
    )
  }

  const onClickLoadMore = () => {
    setLoadingMore(true)
    loadNextPage().then((value) => {
      setLoadingMore(false)
    })
  }

  const renderStateView = (note: Note) => {
    return (
      <Box
        className='action'
        sx={{ cursor: 'pointer' }}
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        gap={1}
      >
        <Box display='flex' flexDirection={'row'} alignItems={'center'} gap={1.5}>
          <Box
            onClick={(e) => {
              e.stopPropagation()
              onClickNoteLike(note)
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '10px',
              backgroundColor: note.isThumbsUp
                ? 'rgba(251, 191, 36, 0.15)'
                : 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid',
              borderColor: note.isThumbsUp
                ? 'rgba(251, 191, 36, 0.3)'
                : 'rgba(255, 255, 255, 0.15)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                backgroundColor: note.isThumbsUp
                  ? 'rgba(251, 191, 36, 0.2)'
                  : 'rgba(255, 255, 255, 0.15)',
                borderColor: note.isThumbsUp
                  ? 'rgba(251, 191, 36, 0.4)'
                  : 'rgba(255, 255, 255, 0.25)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <SVG
              src={note.isThumbsUp ? SVGS.LikeFilledIcon : SVGS.LikeOutlineIcon}
              width={16}
              height={16}
              color={note.isThumbsUp ? '#fbbf24' : 'white'}
            />
            <Typography
              fontSize={'13px'}
              fontWeight={600}
              color='white'
              sx={{ letterSpacing: '-0.01em' }}
            >
              {note.thumbsUp}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
            }}
          >
            <SVG src={SVGS.CommentIcon} width={16} height={16} color='white' />
            <Typography
              fontSize={'13px'}
              fontWeight={600}
              color='white'
              sx={{ letterSpacing: '-0.01em' }}
            >
              {note.comments}
            </Typography>
          </Box>
        </Box>

        {!deleteNote.isLoading && note.authorID === ewUser?.userID && (
          <IconButton
            sx={{
              width: 36,
              height: 36,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '10px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderColor: 'rgba(255, 255, 255, 0.25)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <ActionMenu
              id='topic-item-menu'
              parent={<SVG src={SVGS.MoreIconVert} color={'white'} width={18} height={18} />}
              actions={noteActions(note)}
            />
          </IconButton>
        )}
      </Box>
    )
  }

  return (
    <Box
      ref={scrollRef}
      position='relative'
      flex={1}
      sx={{
        overflowX: 'hidden',
        overflowY: 'auto',
        p: 4,
        backgroundColor: '#f8fafc',
      }}
    >
      <Grid container spacing={2} flex={1}>
        {canLoadNextPage() == true && (
          <Grid item xs={12} key={'header'}>
            <Box display={'flex'} alignItems={'center'} justifyContent={'center'} py={2}>
              {loadingMore ? (
                <LoadingAnimatedView showGif={false} />
              ) : (
                <Button
                  variant='contained'
                  onClick={onClickLoadMore}
                  sx={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    color: '#475569',
                    padding: '10px 28px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    letterSpacing: '-0.01em',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ffffff 0%, #ffffff 100%)',
                      borderColor: '#cbd5e1',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  Load More Photos
                </Button>
              )}
            </Box>
          </Grid>
        )}

        {notes.map((note: Note, index: number) => {
          const isSelected = note.noteID === noteID
          const links = note.egoList.map((e) => e.photoAlbumURL)
          const itemWidth = 233.5
          const itemHeight = 233.5

          const itemInnerWidth = 233.5 - (isSelected ? 16 : 0)
          const itemInnerHeight = 233.5 - (isSelected ? 16 : 0)

          return (
            <Grid item xs={'auto'} key={`${note.noteID}-${index}`}>
              <NoteWrapper
                width={`${itemWidth}px`}
                height={`${itemHeight}px`}
                borderRadius={6}
                key={note.noteID}
                onClick={() => onClickNote(note.noteID)}
                className={isSelected ? 'active' : ''}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Box
                  sx={{
                    width: `${itemInnerWidth}px`,
                    height: `${itemInnerHeight}px`,
                  }}
                >
                  {note.egoList.length > 0 ? (
                    <AlbumCollage
                      links={links}
                      rowHeight={
                        Math.min(links.length, COLLAGE_ITEMS_LIMIT) <= 2
                          ? itemInnerHeight
                          : 0.5 * itemInnerHeight
                      }
                      applyLimit
                    />
                  ) : (
                    <BackgroundImage
                      sx={{ minHeight: `${itemInnerHeight}px` }}
                      url={
                        note.egoList.length > 0
                          ? note.egoList[note.egoList.length - 1].photoAlbumURL
                          : IMAGES.topicPreviewPlaceholder
                      }
                      onClick={() => onClickNote(note.noteID)}
                    />
                  )}
                  <Box className='overlay' />

                  {renderStateView(note)}
                </Box>
              </NoteWrapper>
            </Grid>
          )
        })}
      </Grid>
      <RModal
        open={modalOpened}
        onClose={onCloseModal}
        title='Delete Photo'
        description='Are you sure you want to delete this photo?'
        submitText='Delete'
        onSubmit={onDeletePhoto}
      />
      {slideMode && selectedNote && selectedNote.egoList.length > 0 && (
        <ImageSlider photos={photos} note={selectedNote} onClose={onCloseSlider} initialIndex={0} />
      )}
    </Box>
  )
}
